import type { FeedScrollPosition } from '../components/Feed/Feed';
import React from 'react';

export type MapCenter = { lng: number; lat: number } | undefined;

type HomePageLocationContextType = {
  location: string;
  setLocation: (value: string) => void;
  hasInitialFetched: boolean;
  setHasInitialFetched: (value: boolean) => void;
  mapCenter: MapCenter;
  setMapCenter: (value?: MapCenter) => void;
  /**
   * Vytiahnutý spodný list so zoznamom na mobile. Nahradil `activeTab`
   * s prepínačom Zoznam/Mapa - mapa aj zoznam sú odteraz vidieť naraz.
   * Stav drží provider (nie stránka), aby prežil odchod na detail a návrat.
   */
  sheetExpanded: boolean;
  setSheetExpanded: (value: boolean) => void;
  zoom: number;
  setZoom: (value: number) => void;
  /**
   * Kde stál zoznam v paneli, po segmentoch (moj-presov#24). Je tu, nie
   * v stránke, z toho istého dôvodu ako `sheetExpanded`: novinka bez polohy
   * otvára vlastnú stránku a po návrate musí zoznam stáť tam, kde stál.
   * Obyčajný objekt, nie stav - skrolovanie nemá prekresľovať stránku.
   */
  feedScroll: Record<string, FeedScrollPosition | undefined>;
};

const HomePageLocationContext =
  React.createContext<HomePageLocationContextType>(undefined);

const HomePageLocationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [location, setLocation] = React.useState<string>();
  const [mapCenter, setMapCenter] = React.useState<MapCenter>();
  const [hasInitialFetched, setHasInitialFetched] =
    React.useState<boolean>(false);

  const [sheetExpanded, setSheetExpanded] = React.useState(false);
  const [zoom, setZoom] = React.useState<number>(13);
  const [feedScroll] = React.useState<
    Record<string, FeedScrollPosition | undefined>
  >(() => ({}));

  return (
    <HomePageLocationContext
      value={{
        location,
        setLocation,
        hasInitialFetched,
        setHasInitialFetched,
        mapCenter,
        setMapCenter,
        sheetExpanded,
        setSheetExpanded,
        zoom,
        setZoom,
        feedScroll,
      }}
    >
      {children}
    </HomePageLocationContext>
  );
};

export default HomePageLocationProvider;

export const useHomePage = () => {
  const context = React.useContext(HomePageLocationContext);
  if (!context) {
    throw new Error(
      'useHomePage must be used within a HomePageLocationProvider',
    );
  }
  return context;
};
